<script setup lang="ts">
import { ref, computed, watch, watchEffect } from 'vue';
import { z } from 'zod';
import {
    UiForm,
    UiFormField,
    UiInputField,
    UiTextarea,
    UiSelect,
    UiCombobox,
    UiCheckbox,
    UiButton,
    UiText,
} from '@shopexperts/shared';
import ClientSearchCombobox from './ClientSearchCombobox.vue';
import { useExpertProjects } from '../../composables/reviews/useExpertProjects';
import { useProjectValueRanges } from '../../composables/reviews/useProjectValueRanges';
import { useCreateReviewRequest } from '../../composables/reviews/useCreateReviewRequest';

type FormValues = z.infer<typeof schema.value>;

const emit = defineEmits<{
    (e: 'success'): void;
}>();

// Form model
const formModel = ref<FormValues>({
    type: 'new',
    message: "Hey [Client Name],&#10;&#10;I hope you're pleased with how [Project Name] turned out. Could you take 2–3 minutes to leave a quick review? Your feedback helps me improve and build my shopexperts profile so future clients can find me more easily.&#10;&#10;Thank you so much for your support!",
    hiredOnShopexperts: false,
    projectId: null,
    clientEmail: '',
    projectValueRangeId: 0,
    clientFirstName: '',
    clientLastName: '',
    clientCompanyName: '',
    clientCompanyWebsite: '',
});

const formModelInitialValues = ref<FormValues>({
    type: 'new',
    message: `Hey [Client Name], this is [Expert Name] from [Agency Name]. 

I'm working on my new profile on shopexperts.com – a new marketplace for brands to find and hire the world's best Shopify talent.

I hope you've been pleased with our collaboration so far. Could I ask you to take 2-3 minutes to leave a quick review on my profile? 

Your feedback helps me improve and build my shopexperts presence so future clients can find me more easily.

Thank you so much for your support!

[Expert Name]`,
    hiredOnShopexperts: false,
    projectId: null,
    clientEmail: '',
    projectValueRangeId: 0,
    clientFirstName: '',
    clientLastName: '',
    clientCompanyName: '',
    clientCompanyWebsite: '',
});

// Track if we have an existing client selected
const isExistingClient = computed(() => formModel.value.type === 'existing');

// Track if we should show new client fields (email is present but no clientId)
const showNewClientFields = computed(() => formModel.value.type === 'new' && formModel.value.clientEmail?.includes('@'));

// Dynamic schema based on whether client exists
const existingClientSchema = z.object({
    type: z.literal('existing'),
    clientId: z.string(),
    message: z.string().min(10, 'Message must be at least 10 characters'),
    hiredOnShopexperts: z.boolean().optional(),
    projectId: z.number().nullable().optional(),
    projectValueRangeId: z.number().nullable().optional(),
    clientEmail: z.string().nullable().optional(),
    clientFirstName: z.literal(''),
    clientLastName: z.literal(''),
    clientCompanyName: z.literal(''),
    clientCompanyWebsite: z.literal(''),
});

const newClientSchema = z.object({
    type: z.literal('new').optional().default('new'),
    clientId: z.undefined().optional(),
    message: z.string().min(10, 'Message must be at least 10 characters'),
    hiredOnShopexperts: z.boolean().optional().default(false),
    projectId: z.null(),
    projectValueRangeId: z.number(),
    clientEmail: z.email('Invalid email'),
    clientFirstName: z.string(),
    clientLastName: z.string(),
    clientCompanyName: z.string(),
    clientCompanyWebsite: z.string(),
});

const schema = computed(() => {
    return z.discriminatedUnion('type', [existingClientSchema, newClientSchema]).superRefine((values, ctx) => {
        if (values.type === 'existing' && values.hiredOnShopexperts &&
            !projects.value.some(project => project.id === values.projectId && project.can_request_review)) {
            ctx.addIssue({ code: 'custom', path: ['projectId'], message: 'Select an eligible completed project.' });
        }
    });
});

const { projects, isLoadingProjects, projectsError, refetchProjects } = useExpertProjects(computed(() => {
    return formModel.value.clientId ? parseInt(formModel.value.clientId, 10) : undefined;
}));

const { ranges, isLoadingRanges } = useProjectValueRanges();

const rangeOptions = computed(() =>
    ranges.value.map(range => ({
        label: range.label,
        value: range.id.toString(),
    }))
);

const projectOptions = computed(() =>
    projects.value.map(project => ({
        label: `#${project.id} · ${project.name}${project.has_review ? ' — Review submitted' : project.has_pending_review_request ? ' — Request pending' : project.review_invitation_sent_at ? ' — Invitation sent' : !project.can_request_review ? ' — Not eligible' : ''}`,
        value: project.id.toString(),
        disabled: !project.can_request_review,
    }))
);

watch(() => formModel.value.clientId, (newClientId, oldClientId) => {
    if (newClientId !== oldClientId) {
        formModel.value.projectId = null;
        formModel.value.hiredOnShopexperts = false;
        
        formModel.value.clientFirstName = '';
        formModel.value.clientLastName = '';
        formModel.value.clientCompanyName = '';
        formModel.value.clientCompanyWebsite = '';
    }
});

// Watch hired on shopexperts checkbox
watch(() => formModel.value.hiredOnShopexperts, (isHired) => {
    if (!isHired) {
        // Clear project selection when unchecked
        formModel.value.projectId = null;
    } else {
        // Clear project value when checked
        formModel.value.projectValueRangeId = 0;
    }
});

// Create review request mutation
const { submitReviewRequest, isSubmitting } = useCreateReviewRequest();

// Handle form submission
const handleSubmit = async (values: z.infer<typeof schema.value>) => {
    try {
        if (values.type === 'existing') {
            // Existing client - convert clientId string to number
            const clientIdNumber = parseInt(values.clientId, 10);
            await submitReviewRequest({
                client_id: clientIdNumber,
                v2_project_id: values.hiredOnShopexperts ? values.projectId || undefined : undefined,
                project_value_range_id: values.hiredOnShopexperts ? undefined : values.projectValueRangeId || undefined,
                message: values.message,
            });
        } else {
            // New client
            const email = values.clientEmail || '';
            const firstName = values.clientFirstName || '';
            const lastName = values.clientLastName || '';

            await submitReviewRequest({
                client_email: email,
                client_first_name: firstName,
                client_last_name: lastName,
                client_company_name: values.clientCompanyName || '',
                client_company_website: values.clientCompanyWebsite || undefined,
                project_value_range_id: values.projectValueRangeId,
                message: values.message,
            });
        }

        // Emit success to parent
        emit('success');
    } catch (error) {
        // Error handling is done in the mutation
        console.error('[RequestReviewForm] Submit error:', error);
    }
};

const hiredOnShopexperts = computed(() => isExistingClient.value && formModel.value.hiredOnShopexperts);

watchEffect(() => {
    formModel.value.type = formModel.value.clientId ? 'existing' : 'new';
});
</script>

<template>
    <UiForm v-model="formModel" :initial-values="formModelInitialValues" :schema="schema" scroll-to-first-error @submit="handleSubmit">
        <div class="flex flex-col gap-6">
            <ClientSearchCombobox 
                name="clientEmail"
                label="Client's name or email"
                v-model:email="formModel.clientEmail"
                v-model:clientId="formModel.clientId"
            />

            <!-- Hired on Shopexperts (only for existing clients) -->
            <UiFormField v-if="isExistingClient" name="hiredOnShopexperts" label=""
                v-model="formModel.hiredOnShopexperts">
                <UiCheckbox v-model="formModel.hiredOnShopexperts" label="Hired on shopexperts" />
                <UiText as="p" variant="p-small" class="text-se-gray-600 mt-1">
                    Select if you completed a project with this client on shopexperts.
                </UiText>
            </UiFormField>

            <!-- Project Select (only if hired on shopexperts) -->
            <UiFormField v-if="isExistingClient && hiredOnShopexperts" name="projectId"
                :label-capitalize="false" label="Select the project" v-model="formModel.projectId">
                <template #default="{ modelValue, onUpdateModelValue, id, invalid, disabled }">
                    <UiCombobox
                        :model-value="(modelValue as number | undefined)?.toString()"
                        @update:model-value="(val: string | string[] | undefined) => onUpdateModelValue(typeof val === 'string' ? parseInt(val, 10) : undefined)"
                        :id="id"
                        :invalid="invalid"
                        :disabled="disabled || isLoadingProjects"
                        :options="projectOptions" 
                        placeholder="Search by project name or ID"
                        open-on-click
                    />
                </template>
            </UiFormField>

            <div v-if="isExistingClient && hiredOnShopexperts && (projectsError || (!isLoadingProjects && !projects.length))" class="flex flex-col gap-2">
                <template v-if="projectsError">
                    <UiText as="p" variant="p-small" text="Could not load projects. Please try again." />
                    <UiButton variant="outline" label="Retry projects" @click="refetchProjects()" />
                </template>
                <UiText v-else-if="!isLoadingProjects && !projects.length" as="p" variant="p-small" text="No projects are available here for this client. You can uncheck Hired on shopexperts to send a general review request." />
            </div>

            <!-- New Client Fields -->
            <template v-if="showNewClientFields">
                <UiFormField v-model="formModel.clientFirstName" name="clientFirstName" :label-capitalize="false" label="Client's first name">
                    <template #default="{ modelValue, onUpdateModelValue, id, invalid, disabled }">
                        <UiInputField 
                            :model-value="modelValue as string"
                            @update:model-value="onUpdateModelValue"
                            :id="id"
                            :invalid="invalid"
                            :disabled="disabled"
                            placeholder="Enter first name"
                        />
                    </template>
                </UiFormField>

                <UiFormField v-model="formModel.clientLastName" name="clientLastName" :label-capitalize="false" label="Client's last name">
                    <template #default="{ modelValue, onUpdateModelValue, id, invalid, disabled }">
                        <UiInputField 
                            :model-value="modelValue as string"
                            @update:model-value="onUpdateModelValue"
                            :id="id"
                            :invalid="invalid"
                            :disabled="disabled"
                            placeholder="Enter last name"
                        />
                    </template>
                </UiFormField>

                <UiFormField v-model="formModel.clientCompanyName" name="clientCompanyName" :label-capitalize="false" label="Client's brand">
                    <template #default="{ modelValue, onUpdateModelValue, id, invalid, disabled }">
                        <UiInputField 
                            :model-value="modelValue as string"
                            @update:model-value="onUpdateModelValue"
                            :id="id"
                            :invalid="invalid"
                            :disabled="disabled"
                            placeholder="Enter brand name"
                        />
                    </template>
                </UiFormField>

                <UiFormField v-model="formModel.clientCompanyWebsite" name="clientCompanyWebsite"
                    :label-capitalize="false" label="Client's website">
                    <template #default="{ modelValue, onUpdateModelValue, id, invalid, error, disabled }">
                        <UiInputField 
                            :model-value="modelValue as string"
                            @update:model-value="onUpdateModelValue"
                            :id="id"
                            :invalid="invalid"
                            :disabled="disabled"
                            placeholder="https://example.com"
                        />
                        <UiText v-if="error" as="p" variant="p-small" class="text-se-red-600 mt-2">
                            {{ error }}
                        </UiText>
                    </template>
                </UiFormField>
            </template>

            <!-- Project Value (for existing client not hired on shopexperts, or new client) -->
            <UiFormField
                v-if="(isExistingClient && !formModel.hiredOnShopexperts) || showNewClientFields"
                v-model="formModel.projectValueRangeId" name="projectValueRangeId" :label-capitalize="false" label="Project value">
                <template #default="{ modelValue, onUpdateModelValue, id, invalid, disabled }">
                    <UiSelect 
                        :model-value="(modelValue as number | undefined)?.toString()"
                        @update:model-value="(val: string | string[] | undefined) => onUpdateModelValue(typeof val === 'string' ? parseInt(val, 10) : undefined)"
                        :id="id"
                        :invalid="invalid"
                        :disabled="disabled || isLoadingRanges"
                        :options="rangeOptions" 
                        placeholder="Select project value range"
                    />
                </template>
            </UiFormField>

            <!-- Message (always visible) -->
            <UiFormField name="message" :label-capitalize="false" label="Personalize review request" v-model="formModel.message">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
                    <UiTextarea
                        placeholder="Hey [Client Name],&#10;&#10;I hope you're pleased with how [Project Name] turned out. Could you take 2–3 minutes to leave a quick review? Your feedback helps me improve and build my shopexperts profile so future clients can find me more easily.&#10;&#10;Thank you so much for your support!"
                        :rows="6" 
                        :model-value="modelValue as string" 
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate" 
                        :id="id" 
                        :invalid="invalid" 
                        :disabled="disabled" 
                    />
                    <UiText v-if="error" as="p" variant="p-small" class="text-se-red-600 mt-2">
                        {{ error }}
                    </UiText>
                </template>
            </UiFormField>

            <!-- Submit Button -->
            <div class="flex justify-end gap-3 pt-2">
                <UiButton type="submit" variant="primary" label="Send Request" :disabled="isSubmitting" />
            </div>
        </div>
    </UiForm>
</template>
